import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  getLicense, getHistory, getInstancesByLicense,
  disableInstance, enableInstance,
  getPackagesForLicense, requestUpdate, getInstanceUpdateHistory
} from '../../services/api';

const STATUS_COLORS = {
  REQUESTED: { bg: '#fff4e0', color: '#955d00', label: '⏳ Update requested' },
  COMPLETED: { bg: '#e2f3e8', color: '#1a7f37', label: '✓ Updated' },
  FAILED:    { bg: '#fdecea', color: '#c62828', label: '✗ Failed' },
  PENDING:   { bg: '#ffffff', color: '#6b7280', label: '○ Pending' },
};

/** Compare two semver strings. Returns -1, 0 or 1. */
function compareSemver(a, b) {
  if (!a) return -1;
  if (!b) return 1;
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const na = pa[i] || 0, nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

export default function LicenseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [history, setHistory] = useState([]);
  const [instances, setInstances] = useState([]);
  const [packages, setPackages] = useState([]);
  const [days, setDays] = useState(30);
  const [showDisabled, setShowDisabled] = useState(false);  // DB-4497

  // Modals
  const [disableModal, setDisableModal] = useState(null);
  const [reason, setReason] = useState('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [historyModal, setHistoryModal] = useState(null);
  const [updateHistory, setUpdateHistory] = useState([]);

  // Package panel: { instance, pendingUpdate }
  const [packagePanel, setPackagePanel] = useState(null);

  const load = () => {
    getLicense(id).then(setLicense);
    getHistory(id, days).then(pts => {
      const map = {};
      pts.forEach(p => {
        const key = p.timestamp;
        if (!map[key]) map[key] = { timestamp: p.timestamp, total: 0 };
        map[key].total += p.uniqueUsers;
        map[key][p.serverName] = p.uniqueUsers;
      });
      setHistory(Object.values(map).sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)));
    });
    getInstancesByLicense(id).then(setInstances);
    getPackagesForLicense(id).then(pkgs => {
      // Sort by semver descending
      setPackages([...pkgs].sort((a, b) => compareSemver(b.version, a.version)));
    });
  };

  useEffect(() => { load(); }, [id, days]);

  const handleDisable = async () => {
    await disableInstance(disableModal, reason);
    setDisableModal(null); setReason(''); load();
  };

  const handleEnable = async instanceId => {
    await enableInstance(instanceId); load();
  };

  const handleRequestUpdate = async (instanceId, packageId) => {
    await requestUpdate(instanceId, packageId, updateNotes);
    setUpdateNotes('');
    setPackagePanel(null);
    load();
  };

  const openHistory = async inst => {
    setHistoryModal({ instanceId: inst.id, name: inst.serverName });
    const h = await getInstanceUpdateHistory(inst.id);
    setUpdateHistory(h);
  };

  const openPackagePanel = inst => {
    setPackagePanel(inst);
    setUpdateNotes('');
  };

  if (!license) return <p style={{ color: '#6b7280' }}>Loading...</p>;

  const serverNames = [...new Set(history.flatMap(h =>
    Object.keys(h).filter(k => k !== 'timestamp' && k !== 'total')))];
  const COLORS = ['#005cc5', '#1a7f37', '#b45309', '#5f37a8', '#c62828'];

  // DB-4497 : les instances désactivées (active=false) sont séparées ; masquées par
  // défaut, réaffichables via l'interrupteur, et exclues des compteurs.
  const activeInstances   = instances.filter(i => i.active !== false);
  const disabledInstances = instances.filter(i => i.active === false);
  const visibleInstances  = showDisabled
    ? [...activeInstances, ...disabledInstances]
    : activeInstances;

  return (
    <div>
      <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>

      {/* Header KPIs */}
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 0.25rem' }}>{license.clientName}</h1>
          <code style={{ color: '#6b7280', fontSize: '0.85rem' }}>{license.licenseKey}</code>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <KpiBox label="Unique users" value={license.currentUniqueUsers}
                  color={license.compliant ? '#1a7f37' : '#c62828'} />
          <KpiBox label="Quota" value={license.maxUsers} color="#005cc5" />
          <KpiBox label="Window" value={`${license.uniqueUserWindowDays}d`} color="#b45309" />
          <KpiBox label="Instances"
                  value={instances.length ? activeInstances.length : license.instanceCount}
                  color="#5f37a8" />
        </div>
      </div>

      {/* Chart */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontWeight: 600 }}>Unique users over time</span>
          <select value={days} onChange={e => setDays(+e.target.value)} style={selectStyle}>
            {[7, 14, 30, 90].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={history}>
            <CartesianGrid strokeDasharray="3 3" stroke="#cdcdcd" />
            <XAxis dataKey="timestamp" stroke="#8a8f99"
                   tickFormatter={v => new Date(v).toLocaleDateString('fr-FR')}
                   tick={{ fontSize: 11 }} />
            <YAxis stroke="#8a8f99" tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #cdcdcd', color: '#444444' }}
                     labelFormatter={v => new Date(v).toLocaleString('fr-FR')} />
            <Legend />
            <ReferenceLine y={license.maxUsers} stroke="#c62828" strokeDasharray="5 5"
                           label={{ value: 'Quota', fill: '#c62828', fontSize: 11 }} />
            {serverNames.map((name, i) => (
              <Line key={name} type="monotone" dataKey={name}
                    stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Instances table */}
      <div style={{ ...card, marginTop: '1.5rem', overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontWeight: 600 }}>
            Server instances ({activeInstances.length})
          </span>
          {disabledInstances.length > 0 && (
            <label style={{ fontSize: '0.82rem', color: '#6b7280', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={showDisabled}
                     onChange={e => setShowDisabled(e.target.checked)} />
              Afficher les {disabledInstances.length} désactivée{disabledInstances.length > 1 ? 's' : ''}
            </label>
          )}
        </div>
        <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase',
                         borderBottom: '1px solid #cdcdcd' }}>
              {['Server', 'IP', 'Version', 'Users', 'Last seen', 'Update status', 'Actions'].map(h =>
                <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleInstances.map(inst => {
              const upd = inst.latestUpdateStatus
                ? STATUS_COLORS[inst.latestUpdateStatus] : null;
              const isDisabled = inst.active === false;   // DB-4497
              return (
                <tr key={inst.id} style={{ borderBottom: '1px solid #ececec',
                                           ...(isDisabled ? { opacity: 0.5, background: '#fafafa' } : {}) }}>
                  <td style={td}>
                    {inst.serverName}
                    {isDisabled && (
                      <span title={inst.disableReason || 'Instance désactivée'} style={disabledBadge}>
                        Désactivée{inst.disabledAt
                          ? ` · ${new Date(inst.disabledAt).toLocaleDateString('fr-FR')}` : ''}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280', fontSize: '0.82rem' }}>
                    {inst.serverIp}
                  </td>
                  <td style={td}>
                    {inst.currentVersion
                      ? <span style={{ fontFamily: 'monospace', color: '#005cc5' }}>
                          v{inst.currentVersion}
                        </span>
                      : <span style={{ color: '#8a8f99' }}>—</span>}
                  </td>
                  <td style={td}>{inst.lastReportedUsers ?? '—'}</td>
                  <td style={{ ...td, fontSize: '0.82rem', color: '#6b7280' }}>
                    {inst.lastSeenAt
                      ? new Date(inst.lastSeenAt).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td style={td}>
                    {upd
                      ? <span style={{ background: upd.bg, color: upd.color,
                                       padding: '2px 8px', borderRadius: '99px',
                                       fontSize: '0.78rem', fontWeight: 600 }}>
                          {upd.label}
                          {inst.pendingUpdateVersion &&
                            ` → v${inst.pendingUpdateVersion}`}
                          {inst.latestUpdateStatus === 'COMPLETED' && inst.lastUpdateCompletedAt &&
                            <span style={{ fontWeight: 400, marginLeft: '4px' }}>
                              ({new Date(inst.lastUpdateCompletedAt).toLocaleDateString('fr-FR')})
                            </span>}
                        </span>
                      : <span style={{ color: '#8a8f99', fontSize: '0.82rem' }}>—</span>}
                  </td>
                  <td style={{ ...td, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {!isDisabled && (
                      <button onClick={() => openPackagePanel(inst)} style={updateBtn}>
                        📦 Packages
                      </button>
                    )}
                    <button onClick={() => openHistory(inst)} style={linkBtn}>
                      History
                    </button>
                    {isDisabled
                      ? <button onClick={() => handleEnable(inst.id)} style={okBtn}>
                          Enable
                        </button>
                      : <button onClick={() => setDisableModal(inst.id)} style={dangerBtn}>
                          Disable
                        </button>}
                  </td>
                </tr>
              );
            })}
            {visibleInstances.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '1.5rem' }}>
                  {disabledInstances.length > 0
                    ? 'Aucune instance active — coche « Afficher les désactivées » pour les voir.'
                    : 'Aucune instance rattachée à cette licence.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Package panel modal ───────────────────────────────────────── */}
      {packagePanel && (
        <div style={overlay}>
          <div style={{ ...modal, width: '750px', maxWidth: '95vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: '1.25rem' }}>
              <div>
                <h3 style={{ margin: 0, color: '#444444' }}>
                  Available packages — {packagePanel.serverName}
                </h3>
                <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                  Current version:&nbsp;
                  <span style={{ color: '#005cc5', fontFamily: 'monospace' }}>
                    {packagePanel.currentVersion
                      ? `v${packagePanel.currentVersion}` : 'unknown'}
                  </span>
                  {packagePanel.latestUpdateStatus === 'REQUESTED' && (
                    <span style={{ marginLeft: '1rem', background: '#fff4e0',
                                   color: '#955d00', padding: '2px 8px',
                                   borderRadius: '99px', fontSize: '0.78rem' }}>
                      ⏳ Update in progress — buttons locked
                    </span>
                  )}
                </span>
              </div>
              <button onClick={() => setPackagePanel(null)} style={backBtn}>✕ Close</button>
            </div>

            {/* Optional notes field */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Notes for the update request (optional)</label>
              <input style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}
                     placeholder="e.g. scheduled maintenance window..."
                     value={updateNotes}
                     onChange={e => setUpdateNotes(e.target.value)} />
            </div>

            <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#8a8f99', fontSize: '0.78rem',
                             textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
                  {['Version', 'File', 'Target', 'Release Notes', 'Uploaded', 'Action'].map(h =>
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {packages.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '2rem' }}>
                      No packages available for this license
                    </td>
                  </tr>
                )}
                {packages.map(pkg => {
                  const cmp = compareSemver(pkg.version, packagePanel.currentVersion);
                  // cmp > 0 : pkg newer  |  cmp = 0 : same  |  cmp < 0 : older
                  const isOlder    = cmp < 0;
                  const isCurrent  = cmp === 0;
                  const isNewer    = cmp > 0;
                  const canRequest = (isCurrent || isNewer)
                    && packagePanel.latestUpdateStatus !== 'REQUESTED';

                  return (
                    <tr key={pkg.id}
                        style={{
                          borderBottom: '1px solid #ececec',
                          opacity: isOlder ? 0.45 : 1,
                          background: isNewer ? 'rgba(35,40,83,0.08)' : '',
                        }}>
                      <td style={td}>
                        <span style={{
                          fontFamily: 'monospace', fontSize: '0.85rem',
                          color: isNewer ? '#005cc5' : isCurrent ? '#1a7f37' : '#8a8f99',
                          fontWeight: isNewer || isCurrent ? 700 : 400,
                        }}>
                          v{pkg.version}
                        </span>
                        {isCurrent && (
                          <span style={{ marginLeft: '6px', background: '#e2f3e8',
                                         color: '#1a7f37', padding: '1px 6px',
                                         borderRadius: '4px', fontSize: '0.7rem' }}>
                            current
                          </span>
                        )}
                        {isNewer && (
                          <span style={{ marginLeft: '6px', background: '#232853',
                                         color: '#005cc5', padding: '1px 6px',
                                         borderRadius: '4px', fontSize: '0.7rem' }}>
                            newer
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace',
                                   fontSize: '0.78rem', color: '#6b7280' }}>
                        {pkg.fileName}
                      </td>
                      <td style={td}>
                        {pkg.clientName
                          ? <span style={{ color: '#5f37a8', fontSize: '0.82rem' }}>
                              👤 {pkg.clientName}
                            </span>
                          : <span style={{ color: '#1a7f37', fontSize: '0.82rem' }}>
                              🌍 Global
                            </span>}
                      </td>
                      <td style={{ ...td, maxWidth: '200px' }}>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem',
                                       display: 'block', overflow: 'hidden',
                                       textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={pkg.releaseNotes}>
                          {pkg.releaseNotes || '—'}
                        </span>
                      </td>
                      <td style={{ ...td, fontSize: '0.78rem', color: '#8a8f99' }}>
                        {new Date(pkg.uploadedAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td style={td}>
                        {canRequest ? (
                          <button
                            onClick={() => handleRequestUpdate(packagePanel.id, pkg.id)}
                            style={isNewer ? updateBtn : sameVersionBtn}>
                            {isNewer ? '⬆ Request' : '↺ Re-deploy'}
                          </button>
                        ) : isOlder ? (
                          <span style={{ color: '#e6e6e6', fontSize: '0.78rem' }}>
                            older
                          </span>
                        ) : (
                          <span style={{ color: '#8a8f99', fontSize: '0.78rem' }}>
                            🔒 locked
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Disable modal ─────────────────────────────────────────────── */}
      {disableModal && (
        <div style={overlay}>
          <div style={modal}>
            <h3 style={{ margin: '0 0 1rem', color: '#444444' }}>Disable instance</h3>
            <textarea
              style={{ ...selectStyle, width: '100%', height: '80px',
                       resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Reason for disabling..."
              value={reason} onChange={e => setReason(e.target.value)} />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem',
                          justifyContent: 'flex-end' }}>
              <button onClick={() => setDisableModal(null)} style={backBtn}>Cancel</button>
              <button onClick={handleDisable} style={dangerBtn}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Update history modal ──────────────────────────────────────── */}
      {historyModal && (
        <div style={overlay}>
          <div style={{ ...modal, width: '750px', maxWidth: '95vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#444444' }}>
                Update history — {historyModal.name}
              </h3>
              <button onClick={() => setHistoryModal(null)} style={backBtn}>✕ Close</button>
            </div>
            {updateHistory.length === 0
              ? <p style={{ color: '#8a8f99', textAlign: 'center' }}>No update history</p>
              : (
                <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: '#8a8f99', fontSize: '0.78rem',
                                 textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
                      {['Package', 'From', 'To', 'Status', 'Requested', 'Completed', 'Notes'].map(h =>
                        <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {updateHistory.map(u => {
                      const s = STATUS_COLORS[u.status] || STATUS_COLORS.PENDING;
                      return (
                        <tr key={u.id} style={{ borderBottom: '1px solid #ececec' }}>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.82rem' }}>
                            {u.packageName || '—'}
                            <span style={{ color: '#8a8f99', fontSize: '0.72rem' }}> v{u.packageVersion}</span>
                          </td>
                          <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem' }}>
                            {u.fromVersion || '—'}
                          </td>
                          <td style={{ ...td, color: '#005cc5', fontSize: '0.82rem' }}>
                            {u.toVersion}
                          </td>
                          <td style={td}>
                            <span style={{ background: s.bg, color: s.color,
                                           padding: '2px 8px', borderRadius: '99px',
                                           fontSize: '0.75rem' }}>
                              {u.status}
                            </span>
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#6b7280' }}>
                            {u.requestedAt
                              ? new Date(u.requestedAt).toLocaleString('fr-FR') : '—'}<br/>
                            <span style={{ fontSize: '0.72rem' }}>{u.requestedBy}</span>
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#6b7280' }}>
                            {u.completedAt
                              ? new Date(u.completedAt).toLocaleString('fr-FR') : '—'}
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', color: '#6b7280',
                                       maxWidth: '150px' }}>
                            <span title={u.notes}>{u.notes || '—'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiBox({ label, value, color }) {
  return (
    <div style={{ background: '#f5f5f5', padding: '1rem 1.5rem', borderRadius: '10px',
                  textAlign: 'center', minWidth: '110px' }}>
      <div style={{ fontSize: '1.8rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>{label}</div>
    </div>
  );
}

const card        = { background: '#ffffff', borderRadius: '12px', padding: '1.5rem' };
const td          = { padding: '0.75rem', color: '#444444' };
const labelStyle  = { display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' };
const selectStyle = { background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444',
                      padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem' };
const backBtn     = { padding: '0.4rem 1rem', background: '#ffffff', border: '1px solid #cdcdcd',
                      color: '#6b7280', borderRadius: '8px', cursor: 'pointer',
                      marginBottom: '1.5rem', display: 'inline-block' };
const dangerBtn   = { padding: '0.35rem 0.85rem', background: '#fdecea', border: '1px solid #f3b8b4',
                      color: '#b3261e', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' };
const okBtn       = { padding: '0.35rem 0.85rem', background: '#e2f3e8', border: '1px solid #a7d7b4',
                      color: '#1a7f37', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' };
const linkBtn     = { padding: '0.35rem 0.85rem', background: 'transparent',
                      border: '1px solid #cdcdcd', color: '#005cc5', borderRadius: '6px',
                      cursor: 'pointer', fontSize: '0.82rem' };
// DB-4497 : badge des instances désactivées dans la liste « Server instances ».
const disabledBadge = { marginLeft: '8px', background: '#f0f0f0', color: '#8a8f99',
                        border: '1px solid #dcdcdc', padding: '1px 8px', borderRadius: '99px',
                        fontSize: '0.7rem', fontWeight: 600, verticalAlign: 'middle',
                        whiteSpace: 'nowrap' };
// DB-4495 : ces styles étaient utilisés par les modales (Packages/History/Disable) mais
// n'étaient définis nulle part dans ce fichier -> ReferenceError au rendu -> écran blanc.
const overlay     = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal       = { background: '#ffffff', borderRadius: '12px', padding: '2rem',
                      maxWidth: 'calc(100vw - 2rem)', boxSizing: 'border-box',
                      maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const updateBtn   = { padding: '0.35rem 0.85rem', background: '#e3ebf5',
                      border: '1px solid #232853', color: '#005cc5', borderRadius: '6px',
                      cursor: 'pointer', fontSize: '0.82rem' };
const sameVersionBtn = { padding: '0.35rem 0.85rem', background: '#ffffff',
                         border: '1px solid #cdcdcd', color: '#6b7280', borderRadius: '6px',
                         cursor: 'pointer', fontSize: '0.82rem' };
